/**
 * Sample data for mock mode — same people and texts as the approved mockup (Classic4), with times
 * relative to "now" so wait times tick like the real thing.
 */
import { DEFAULT_SETTINGS } from '../lib/inbox'
import type {
  AiConfig,
  AiInsight,
  CannedReply,
  Channel,
  ChatMessage,
  ContactTagRef,
  ConversationDetail,
  ConversationKind,
  InboxSettings,
  LearnItem,
  OrderDraft,
  PosLink,
  PosOrder,
  RowTag,
  StaffRef,
  SystemEvent,
  TeamMember,
  ThreadItem,
} from './types'
import { MOCK_AVATARS } from './mockAvatars'
import { createMockMedia } from './mockLibrary'

/** Sticker placeholder (transparent, like a LINE sticker) — the real ones come from LINE's sticker CDN. */
const MOCK_STICKER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240">' +
      '<circle cx="120" cy="124" r="92" fill="#f6c453" stroke="#3b2f1e" stroke-width="6"/>' +
      '<circle cx="88" cy="108" r="11" fill="#3b2f1e"/><circle cx="152" cy="108" r="11" fill="#3b2f1e"/>' +
      '<path d="M78 148 q42 38 84 0" fill="none" stroke="#3b2f1e" stroke-width="8" stroke-linecap="round"/>' +
      '<circle cx="66" cy="140" r="12" fill="#f29a8a"/><circle cx="174" cy="140" r="12" fill="#f29a8a"/></svg>',
  )

/** Portrait placeholder photo (like a customer's product shot) so the mockup shows real image sizing. */
const MOCK_PHOTO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800" viewBox="0 0 600 800">' +
      '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e9f3ec"/><stop offset="1" stop-color="#b9d9c4"/></linearGradient></defs>' +
      '<rect width="600" height="800" fill="url(#g)"/><rect x="150" y="180" width="300" height="420" rx="28" fill="#2f8a5b"/>' +
      '<circle cx="300" cy="330" r="80" fill="#f4c152"/><rect x="200" y="460" width="200" height="24" rx="12" fill="#e9f3ec"/></svg>',
  )

/** A customer's menu list with ticks and numbers (อ่านเมนูจากแชท demo, chat c101; api/menuread.ts reads it). */
const MENU_SHEET_ROWS: [string, string, string | null][] = [
  ['P1', 'กะเพราไก่สับ', '5'],
  ['P2', 'ไก่ย่างโคชูจัง', '5'],
  ['P3', 'ไก่สับกระเทียมพริกไทย', '4'],
  ['P4', 'แกงเขียวหวานไก่', null],
  ['P5', 'แซลมอนย่างซีอิ๊วญี่ปุ่น', '3'],
  ['P6', 'ปลาดอลลี่นึ่งมะนาว', null],
  ['P7', 'สเต๊กหมูพริกไทยดำ', '4'],
  ['P8', 'ผัดผักรวมเต้าหู้', ''],
]
const MOCK_MENU_SHEET =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800" viewBox="0 0 600 800" font-family="sans-serif">' +
      '<rect width="600" height="800" fill="#fbfaf6"/>' +
      '<text x="300" y="66" font-size="30" font-weight="700" text-anchor="middle" fill="#1f3d31">เมนูสัปดาห์นี้</text>' +
      '<text x="300" y="104" font-size="18" text-anchor="middle" fill="#6b7a73">ติ๊กเมนูที่ต้องการ แล้วเขียนจำนวนกล่อง</text>' +
      MENU_SHEET_ROWS.map(([code, name, n], i) => {
        const y = 132 + i * 80
        const tick = n === null ? '' : `<path d="M34 ${y + 40}l12 13 24-28" stroke="#d62a1e" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
        const num = n ? `<text x="540" y="${y + 52}" font-size="38" font-weight="700" font-style="italic" text-anchor="middle" fill="#d62a1e">${n}</text>` : ''
        return (
          `<rect x="20" y="${y}" width="560" height="70" rx="12" fill="${i % 2 ? '#ffffff' : '#f2f5f1'}" stroke="#e0e6e1"/>` +
          `<text x="92" y="${y + 44}" font-size="22" font-weight="700" fill="#0e7a5c">${code}</text>` +
          `<text x="146" y="${y + 44}" font-size="22" fill="#1b2a23">${name}</text>` +
          tick +
          num
        )
      }).join('') +
      '</svg>',
  )

/** A bank-transfer slip look-alike (no real bank data) for the slip-check button and badge. */
const MOCK_SLIP =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="900" viewBox="0 0 600 900" font-family="sans-serif">' +
      '<rect width="600" height="900" fill="#ffffff"/><rect width="600" height="210" fill="#4b2a86"/>' +
      '<circle cx="300" cy="105" r="46" fill="#ffffff"/><path d="M278 106l16 16 30-32" stroke="#1f9d62" stroke-width="10" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<text x="300" y="290" font-size="34" font-weight="700" text-anchor="middle" fill="#1b1b1b">โอนเงินสำเร็จ</text>' +
      '<text x="300" y="350" font-size="46" font-weight="700" text-anchor="middle" fill="#1b1b1b">2,255.00</text>' +
      '<rect x="60" y="420" width="480" height="2" fill="#e5e5e5"/>' +
      '<rect x="60" y="460" width="200" height="18" rx="9" fill="#d9d9d9"/><rect x="340" y="460" width="200" height="18" rx="9" fill="#bdbdbd"/>' +
      '<rect x="60" y="510" width="160" height="18" rx="9" fill="#d9d9d9"/><rect x="300" y="510" width="240" height="18" rx="9" fill="#bdbdbd"/>' +
      '<rect x="60" y="560" width="220" height="18" rx="9" fill="#d9d9d9"/><rect x="360" y="560" width="180" height="18" rx="9" fill="#bdbdbd"/>' +
      '<rect x="220" y="650" width="160" height="160" fill="#1b1b1b"/><rect x="236" y="666" width="128" height="128" fill="#ffffff"/>' +
      '<rect x="256" y="686" width="36" height="36" fill="#1b1b1b"/><rect x="308" y="686" width="36" height="36" fill="#1b1b1b"/><rect x="256" y="738" width="36" height="36" fill="#1b1b1b"/><rect x="316" y="746" width="20" height="20" fill="#1b1b1b"/>' +
      '</svg>',
  )

export interface MockDb {
  now: number
  meId: string
  team: TeamMember[]
  settings: InboxSettings
  conversations: Map<string, ConversationDetail>
  threads: Map<string, ThreadItem[]>
  orderDrafts: Map<string, OrderDraft>
  seq: number
}

type StaffKey = 'ploy' | 'jit' | 'siri' | 'waranya' | 'siriporn'

export const STAFF: Record<StaffKey, StaffRef & { initial: string }> = {
  ploy: { id: 'ploy', name: 'พลอย', initial: 'พ' },
  jit: { id: 'jit', name: 'จิต', initial: 'จ' },
  siri: { id: 'siri', name: 'ศิริ', initial: 'ศ' },
  waranya: { id: 'waranya', name: 'วรัญญา', initial: 'ว' },
  siriporn: { id: 'siriporn', name: 'ศิริพร', initial: 'ศ' },
}

const ref = (k: StaffKey): StaffRef => ({ id: STAFF[k].id, name: STAFF[k].name })

export const CANNED: CannedReply[] = [
  { id: 'ask_zip', label: 'ขอรหัสไปรษณีย์', text: 'รบกวนขอรหัสไปรษณีย์ด้วยนะคะ จะได้เช็คค่าส่งและรอบส่งให้ค่ะ' },
  { id: 'order_link', label: 'ส่งลิงก์สั่งซื้อ', text: 'สั่งได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order' },
]

/** Team labels on customers (api/migrations/0017_collab.sql starter set), most important first. */
export const MOCK_TAGS: (ContactTagRef & { sort: number })[] = [
  { id: 't1', name: 'แพ้อาหาร', tone: 'crit', sort: 10 },
  { id: 't2', name: 'รอชำระ', tone: 'warn', sort: 20 },
  { id: 't3', name: 'VIP', tone: 'violet', sort: 30 },
  { id: 't4', name: 'สนใจแพ็กใหญ่', tone: 'ok', sort: 40 },
  { id: 't5', name: 'ลูกค้าใหม่', tone: 'info', sort: 50 },
]

/** Put team labels on a mock customer: `tags` sorted by importance, the row chip (`tag`) = the first one. */
export function applyMockTags(c: ConversationDetail, ids: string[], defs: (ContactTagRef & { sort: number })[] = MOCK_TAGS): void {
  const tags = defs
    .filter((d) => ids.includes(d.id))
    .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id))
    .map(({ id, name, tone }) => ({ id, name, tone }))
  c.tags = tags
  c.tag = tags[0] ? { kind: tags[0].tone, label: tags[0].name } : null
}

export const PACKAGES = [
  { id: 'trial3', name: 'ทดลอง 3 วัน', price: 1490 },
  { id: 'd7m1', name: '7 วัน · 1 มื้อ/วัน', price: 1190 },
  { id: 'd7m2', name: '7 วัน · 2 มื้อ/วัน', price: 2190 },
]

const SLOTS = ['10:00–12:00', '14:00–16:00', '17:00–19:00']

// ---------------------------------------------------------------- thread spec

/** [seconds ago, who, text, extra] */
type Line =
  | [number, 'in', string]
  | [number, 'in-img', string]
  | [number, 'in-stk', string]
  | [number, 'in-loc', string]
  | [number, 'staff', string, StaffKey]
  | [number, 'ai', string, string]
  | [number, 'handoff', string, StaffKey]
  /** sent by the AI in charge of the chat (AI mode, docs/ai-mode.md): author ai, no topic */
  | [number, 'bot', string]
  /** AI mode timeline line: ai_on / ai_off (reason code) */
  | [number, 'sys', 'ai_on' | 'ai_off', string | null, StaffKey | null]

interface Spec {
  id: string
  name: string
  initial?: string
  channel: Channel
  kind?: ConversationKind
  staff: StaffKey | null
  status?: 'open' | 'closed'
  hot?: boolean
  tag?: RowTag | null
  /** Team labels (MOCK_TAGS ids). */
  tags?: string[]
  unread?: number
  followUp?: boolean
  thread: Line[]
  pos?: PosLink | null
  ai?: AiInsight | null
  ad?: string | null
  phone?: string | null
}

const MIN = 60
const HOUR = 3600
const DAY = 86400
/** The API's `follow_up_window_days`: a chat stays in ทักตาม at most this long after our last message. */
export const MOCK_FOLLOW_UP_WINDOW_DAYS = 7

function initialOf(name: string): string {
  const s = name.replace(/^(คุณ|Mr\.?|Ms\.?|Mrs\.?)\s*/i, '').trim()
  return (s[0] ?? '?').toUpperCase()
}

function posLink(
  now: number,
  id: string,
  orders: [daysAgo: number, pkg: string, total: number, ch: Channel, staff: StaffKey][],
  opts: { allergies?: string[]; favourites?: string[]; interval?: number | null; numbers?: string[] } = {},
): PosLink {
  const recent: PosOrder[] = orders.map(([d, pkg, total, ch, st], i) => ({
    id: `${id}-o${i}`,
    number: opts.numbers?.[i] ?? `#${24300 - i * 441 - id.length * 7}`,
    at: new Date(now - d * DAY * 1000).toISOString(),
    package: pkg,
    total,
    status: 'ส่งถึงแล้ว',
    channel: ch,
    staff: ref(st),
  }))
  const sum = orders.reduce((a, o) => a + o[2], 0)
  return {
    customer_id: id,
    url: `https://pos.example.invalid/customer/${id}`,
    segment: orders.length > 0 ? 'returning' : 'new',
    allergies: opts.allergies ?? [],
    stats: {
      orders_count: orders.length,
      total_spent: sum,
      avg_order: orders.length ? Math.round(sum / orders.length) : 0,
      last_order_at: recent[0]?.at ?? null,
      usual_interval_days: opts.interval ?? null,
      favourites: opts.favourites ?? [],
    },
    recent_orders: recent,
  }
}

function build(db: MockDb, s: Spec): void {
  const now = db.now
  const at = (ago: number) => new Date(now - ago * 1000).toISOString()
  const items: ThreadItem[] = s.thread.map((l, i): ThreadItem => {
    const id = `${s.id}-m${i}`
    const base = { id, conversation_id: s.id, at: at(l[0]) }
    if (l[1] === 'handoff') {
      const ev: SystemEvent = { ...base, kind: 'system', event: 'ai_handoff', staff: ref(l[3]), reason: l[2] }
      return ev
    }
    if (l[1] === 'sys') {
      const ev: SystemEvent = { ...base, kind: 'system', event: l[2], staff: l[4] ? ref(l[4]) : null, reason: l[3] }
      return ev
    }
    const inbound = l[1] === 'in' || l[1] === 'in-img' || l[1] === 'in-stk' || l[1] === 'in-loc'
    const msg: ChatMessage = {
      ...base,
      kind: 'message',
      direction: inbound ? 'in' : 'out',
      type: l[1] === 'in-img' ? 'image' : l[1] === 'in-stk' ? 'sticker' : l[1] === 'in-loc' ? 'location' : s.kind === 'comment' && inbound ? 'comment' : 'text',
      text: l[1] === 'in-img' || l[1] === 'in-stk' || l[1] === 'in-loc' ? null : l[2],
      attachments:
        l[1] === 'in-img'
          ? [{ type: 'image', url: /สลิป/.test(l[2]) ? MOCK_SLIP : /เมนู/.test(l[2]) ? MOCK_MENU_SHEET : MOCK_PHOTO, name: l[2] }]
          : l[1] === 'in-stk'
            ? [{ type: 'sticker', url: MOCK_STICKER, name: null }]
            : l[1] === 'in-loc'
              ? [{ type: 'location', url: null, name: l[2], lat: 13.668431, lng: 100.634617, address: 'ถนนบางนา-ตราด แขวงบางนาใต้ เขตบางนา กรุงเทพมหานคร 10260' }]
              : [],
      author:
        l[1] === 'staff'
          ? { kind: 'staff', staff: ref(l[3]) }
          : l[1] === 'ai'
            ? { kind: 'ai', topic: l[3] }
            : l[1] === 'bot'
              ? { kind: 'ai', topic: null }
              : { kind: 'contact' },
      read: !inbound,
    }
    return msg
  })
  const msgs = items.filter((x): x is ChatMessage => x.kind === 'message')
  const last = msgs[msgs.length - 1]
  if (!last) throw new Error(`mock ${s.id} has no message`)
  const lastOut = [...msgs].reverse().find((m) => m.direction === 'out')
  const needsReply = last.direction === 'in'
  const firstWaiting = needsReply ? msgs.find((m) => m.direction === 'in' && (!lastOut || m.at > lastOut.at)) : undefined
  const followUpOn = s.followUp ?? true
  const status = s.status ?? 'open'
  const dueMs = !needsReply && followUpOn ? Date.parse(last.at) + db.settings.follow_up_after_min * 60000 : null
  const preview = last.type === 'image' ? `[รูปภาพ] ${last.attachments[0]?.name ?? ''}`.trim() : (last.text ?? '')
  const detail: ConversationDetail = {
    id: s.id,
    channel: s.channel,
    kind: s.kind ?? 'dm',
    status,
    contact: {
      id: `ct-${s.id}`,
      name: s.name,
      initial: s.initial ?? initialOf(s.name),
      avatar_url: MOCK_AVATARS[s.id] ?? null,
      channel: s.channel,
      phone_masked: s.phone ?? null,
      username: s.channel === 'ig' ? s.name : null,
      first_seen_at: at(90 * DAY),
      last_seen_at: last.at,
    },
    last_message: { text: preview, type: last.type, direction: last.direction, at: last.at },
    last_message_at: last.at,
    needs_reply: needsReply,
    waiting_since: firstWaiting?.at ?? null,
    follow_up_due_at: dueMs === null ? null : new Date(dueMs).toISOString(),
    // like the API: the follow-up window ends 7 days after our last message (`follow_up_window_days`)
    follow_up_until: dueMs === null ? null : new Date(Date.parse(last.at) + MOCK_FOLLOW_UP_WINDOW_DAYS * DAY * 1000).toISOString(),
    unread: s.unread ?? 0,
    assigned_staff: s.staff ? ref(s.staff) : null,
    tag: s.tag ?? null,
    hot: s.hot ?? false,
    account: {
      id: `acc-${s.channel}`,
      channel: s.channel,
      name: s.channel === 'line' ? 'LINE OA Cleanfit' : s.channel === 'web' ? 'แชทเว็บ' : 'Cleanfit Healthy',
    },
    pos: s.pos ?? null,
    ad_source: s.ad
      ? { source: 'ADS', ad_id: `ad-${s.id}`, ref: null, type: 'OPEN_THREAD', first_at: at(10 * DAY), label: s.ad }
      : null,
    auto_close_off: false,
    follow_up: { enabled: followUpOn, after_minutes: db.settings.follow_up_after_min },
    staff_read_at: null,
    contact_read_at: null,
    ai: s.ai ?? null,
  }
  if (s.tags) applyMockTags(detail, s.tags)
  db.conversations.set(s.id, detail)
  db.threads.set(s.id, items)
}

function genericDraft(pos: PosLink | null): OrderDraft {
  return {
    addresses: pos ? [{ id: 'home', label: 'บ้าน', text: 'ที่อยู่จากสมุดที่อยู่ลูกค้าใน POS', by_ai: false }] : [],
    address_id: pos ? 'home' : null,
    packages: PACKAGES,
    package_id: null,
    package_by_ai: false,
    menu_count_label: '0 มื้อ',
    allergy_filter: null,
    menu: [],
    swaps: [],
    swap_reason: null,
    ship: { via: 'Lalamove', fee: 65, source: 'จากระบบค่าส่ง', date_label: 'พรุ่งนี้', cutoff: 'สั่งและชำระก่อน 21:30 ได้ของพรุ่งนี้' },
    slots: SLOTS,
    slot: null,
    source_label: null,
  }
}

/** What the server builds from the approved round for "ขอรายชื่อเมนู" (api side, not written by the LLM). */
export const MENU_ROUND_DRAFT = [
  'เมนูรอบนี้ค่ะ',
  'ไก่: กะเพราไก่สับ · ไก่ย่างโคชูจัง · ไก่สับกระเทียมพริกไทย · ไก่คั่วพริกเกลือ',
  'หมู: หมูผัดกระเทียม · สเต๊กหมูพริกไทยดำ',
  'ปลา: แซลมอนย่างซีอิ๊วญี่ปุ่น · ปลาแซลมอนผัดพริกไทยดำ',
  'ทะเล: กุ้งผัดพริกเกลือ · ผัดไทยกุ้ง',
  'เส้น: สปาเก็ตตี้โฮลวีทไก่กระเทียม · เส้นบุกผัดขี้เมาไก่',
  'เมนูใหม่รอบนี้: ไก่คั่วพริกเกลือ · ปลาแซลมอนผัดพริกไทยดำ · เส้นบุกผัดขี้เมาไก่',
  'สนใจเมนูไหนบอกแอดมินได้เลยนะคะ',
].join('\n')

const steps = (...xs: [string, string | null, boolean?, boolean?][]) =>
  xs.map(([text, note, byAi, done], i) => ({ id: `s${i}`, text, note, by_ai: byAi ?? false, done: done ?? false }))

export function createMockDb(now: number = Date.now()): MockDb {
  const db: MockDb = {
    now,
    meId: 'ploy',
    team: [],
    settings: { ...DEFAULT_SETTINGS },
    conversations: new Map(),
    threads: new Map(),
    orderDrafts: new Map(),
    seq: 1000,
  }

  // ------------------------------------------------ ต้องตอบ (7) — exactly the mockup rows
  build(db, {
    id: 'c101',
    name: 'Pim Chanok',
    channel: 'fb',
    staff: 'ploy',
    hot: true,
    unread: 1,
    tags: ['t5'],
    ad: 'แอด FB · ส่งทั่วประเทศ',
    thread: [
      [210, 'in', 'สวัสดีค่ะ'],
      [206, 'in', 'ส่งต่างจังหวัดได้ไหมคะ อยู่เชียงใหม่'],
    ],
    ai: {
      close_chance: 64,
      next_steps: steps(['บอกว่าส่งเชียงใหม่ได้', 'อยู่ในร่างคำตอบแล้ว'], ['ขอรหัสไปรษณีย์', 'ใช้คำนวณค่าส่งจริง']),
      draft:
        'ส่งเชียงใหม่ได้ค่ะ จัดส่งแบบแช่เย็นผ่าน DHL ได้รับภายใน 1–2 วันค่ะ รบกวนขอรหัสไปรษณีย์ด้วยนะคะ จะได้แจ้งค่าส่งให้ค่ะ',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  build(db, {
    id: 'c102',
    name: 'คุณมายด์',
    initial: 'ม',
    channel: 'line',
    staff: 'ploy',
    hot: true,
    tags: ['t1', 't3'],
    phone: '•••-•••-4521',
    ad: 'แอด LINE · แพ็ก 7 วัน ก.ย.',
    thread: [
      [310, 'in', 'สวัสดีค่ะ สนใจแพ็กอาหารคลีน 7 วันค่ะ'],
      [
        300,
        'ai',
        'สวัสดีค่ะคุณมายด์ แพ็ก 7 วันมีแบบ 1 มื้อ/วัน และ 2 มื้อ/วันค่ะ ดูเมนูและราคาทั้งหมดได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order',
        'เมนู/ราคา',
      ],
      [132, 'in', 'ส่งแถวบางนาค่าส่งเท่าไหร่คะ'],
      [124, 'in', 'แล้วแพ้กุ้ง เลือกเมนูเองได้ไหมคะ'],
      [122, 'handoff', 'ถาม 2 เรื่อง และมีเรื่องแพ้อาหาร', 'ploy'],
    ],
    pos: posLink(
      now,
      'pos-mayd',
      [
        [44, 'แพ็ก 7 วัน 2 มื้อ/วัน', 2190, 'line', 'ploy'],
        [59, 'แพ็ก 7 วัน 2 มื้อ/วัน', 2190, 'line', 'jit'],
        [85, 'ทดลอง 3 วัน', 1490, 'fb', 'ploy'],
      ],
      {
        allergies: ['แพ้กุ้ง'],
        favourites: ['อกไก่ย่างเทริยากิ', 'แซลมอนย่าง'],
        interval: 14,
        numbers: ['#24318', '#23877', '#23102'],
      },
    ),
    ai: {
      close_chance: 72,
      next_steps: steps(
        ['บอกค่าส่งและรอบส่ง', 'อยู่ในร่างคำตอบแล้ว', false, true],
        ['ขอรหัสไปรษณีย์', 'ยืนยันว่าส่งที่บ้านเดิม บางนา'],
        ['เปลี่ยนเมนูที่มีกุ้ง', 'AI สลับในร่างออเดอร์แล้ว รอตรวจ', true],
      ),
      draft:
        'ส่งแถวบางนา ค่าส่ง 65 บาทค่ะ ถ้าสั่งและชำระก่อน 21:30 วันนี้ จะได้รับของพรุ่งนี้ค่ะ ส่วนเรื่องแพ้กุ้ง แอดมินเปลี่ยนเป็นเมนูที่ไม่มีกุ้งให้ได้เลยค่ะ รบกวนขอรหัสไปรษณีย์ด้วยนะคะ',
      canned: CANNED,
      cart: { title: 'AI เตรียมตะกร้าไว้แล้ว', package: 'แพ็ก 7 วัน 2 มื้อ/วัน', total: 2255, source: 'จากแชทนี้' },
      quick: null,
      draft_source: null,
    },
  })
  db.orderDrafts.set('c102', {
    addresses: [
      { id: 'home', label: 'บ้าน', text: 'ซ.บางนา-ตราด 25 แขวงบางนา เขตบางนา กทม. 10260', by_ai: true },
      { id: 'work', label: 'ที่ทำงาน', text: 'ถ.สาทรใต้ เขตสาทร กทม. 10120', by_ai: false },
    ],
    address_id: 'home',
    packages: PACKAGES,
    package_id: 'd7m2',
    package_by_ai: true,
    menu_count_label: '14 มื้อ',
    allergy_filter: { label: 'ไม่มีกุ้ง', on: true },
    menu: [
      { name: 'อกไก่ย่างซอสเทริยากิ', qty: 2 },
      { name: 'แซลมอนย่างซีอิ๊วญี่ปุ่น', qty: 2 },
      { name: 'ปลาดอลลี่นึ่งมะนาว', qty: 2 },
      { name: 'สเต๊กหมูพริกไทยดำ', qty: 2 },
    ],
    swaps: [
      { name: 'ข้าวกล้องผัดกะเพราอกไก่', replaces: 'ผัดไทยกุ้ง', qty: 2 },
      { name: 'ไก่ผัดเม็ดมะม่วง', replaces: 'ยำวุ้นเส้นกุ้ง', qty: 2 },
      { name: 'สปาเก็ตตี้โฮลวีทไก่กระเทียม', replaces: 'สปาเก็ตตี้กุ้ง', qty: 2 },
    ],
    swap_reason: 'สลับเพราะแพ้กุ้ง',
    ship: {
      via: 'Lalamove',
      fee: 65,
      source: 'จากระบบค่าส่ง',
      date_label: 'พรุ่งนี้',
      cutoff: 'สั่งและชำระก่อน 21:30 ได้ของพรุ่งนี้',
    },
    slots: SLOTS,
    slot: SLOTS[0] ?? null,
    source_label: 'แอด LINE · แพ็ก 7 วัน ก.ย.',
  })

  build(db, {
    id: 'c103',
    name: 'คุณเบียร์',
    initial: 'บ',
    channel: 'line',
    staff: 'siri',
    unread: 1,
    thread: [
      [50 * MIN, 'in-stk', 'sticker'],
      [49 * MIN, 'in-loc', 'คอนโดบางนา'],
      [40 * MIN, 'staff', 'ยอดรวม 2,255 บาทค่ะ โอนเข้าพร้อมเพย์ตาม QR ได้เลยค่ะ', 'siri'],
      [125, 'in-img', 'สลิปโอนเงิน'],
      [70, 'in-img', 'รูปหน้าบ้าน'],
    ],
    pos: posLink(now, 'pos-beer', [[30, 'แพ็ก 7 วัน 1 มื้อ/วัน', 1190, 'line', 'siri']]),
    ai: {
      close_chance: null,
      next_steps: steps(['ยืนยันว่าได้รับเงินแล้ว', 'ระบบเช็คสลิปผ่านแล้ว ยอดตรง', true]),
      draft: 'ได้รับสลิปแล้วค่ะ ยอดตรง ขอบคุณค่ะ ออเดอร์จะจัดส่งพรุ่งนี้รอบ 10:00–12:00 นะคะ',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  // the slip above was checked by staff (badge under the picture); the house photo is not checked yet (button)
  const beerSlip = db.threads.get('c103')?.find((x): x is ChatMessage => x.kind === 'message' && x.type === 'image')
  if (beerSlip) {
    beerSlip.slip = {
      status: 'ok',
      label: 'สลิปผ่าน',
      amount: 2255,
      bank: 'SCB',
      slip_at: new Date(now - 3 * MIN * 1000).toISOString(),
      trans_ref: '016269140211ABC',
      dup: null,
      note: null,
      via: 'staff',
      checked_at: new Date(now - 100 * 1000).toISOString(),
      can_retry: false,
    }
  }

  build(db, {
    id: 'c104',
    name: 'คุณบอส',
    initial: 'บ',
    channel: 'line',
    staff: 'jit',
    hot: true,
    unread: 1,
    thread: [[90, 'in', 'ช่วงเทศกาลกินเจมีเมนูเจไหมครับ']],
    pos: posLink(now, 'pos-boss', [[20, 'ทดลอง 3 วัน', 1490, 'line', 'jit']]),
    ai: {
      close_chance: 58,
      next_steps: steps(['ส่งเมนูเจช่วงเทศกาล', 'อยู่ในร่างคำตอบแล้ว'], ['ถามจำนวนวันที่อยากทาน', null]),
      draft: 'มีเมนูเจช่วงเทศกาลค่ะ สั่งล่วงหน้าได้ตั้งแต่วันนี้ ดูเมนูเจทั้งหมดได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  build(db, {
    id: 'c105',
    name: 'คอมเมนต์ใต้แอด',
    initial: 'ค',
    channel: 'fb',
    kind: 'comment',
    staff: 'waranya',
    hot: true,
    ad: 'แอด FB · แพ็กทดลอง',
    thread: [[65, 'in', 'สนใจค่ะ ราคาเท่าไหร่']],
    ai: {
      close_chance: 41,
      next_steps: steps(['ตอบใต้คอมเมนต์ และทักแชทส่วนตัว', null]),
      draft: 'สวัสดีค่ะ แพ็กทดลอง 3 วันเริ่มต้น 1,490 บาทค่ะ ส่งรายละเอียดทางแชทให้แล้วนะคะ',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  build(db, {
    id: 'c106',
    name: 'ต้นกล้า',
    channel: 'ig',
    staff: 'siri',
    unread: 1,
    thread: [
      [2 * DAY, 'staff', 'ได้รับออเดอร์แล้วค่ะ จัดส่งวันเสาร์นะคะ', 'siri'],
      [35, 'in', 'ของจะมาถึงกี่โมงครับ'],
    ],
    pos: posLink(now, 'pos-tonkla', [[2, 'แพ็ก 7 วัน 1 มื้อ/วัน', 1190, 'ig', 'siri']]),
    ai: {
      close_chance: null,
      next_steps: steps(['แจ้งเวลาถึงจาก Lalamove', 'ดึงเวลาถึงสดจากระบบส่ง', true]),
      draft: 'ออเดอร์อยู่ระหว่างจัดส่งค่ะ คาดว่าถึงประมาณ 11:30 นะคะ',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  build(db, {
    id: 'c107',
    name: 'ผู้เยี่ยมชมเว็บ',
    initial: 'ผ',
    channel: 'web',
    staff: 'waranya',
    unread: 1,
    thread: [[20, 'in', 'สั่งไปแล้ว แก้ที่อยู่ได้ไหมคะ']],
    ai: {
      close_chance: null,
      next_steps: steps(['ขอเลขคำสั่งซื้อ', null], ['แก้ที่อยู่ใน POS', null]),
      draft: 'แก้ที่อยู่ได้ค่ะ รบกวนแจ้งเลขคำสั่งซื้อพร้อมที่อยู่ใหม่ด้วยนะคะ',
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
    },
  })

  // c107: the website said the payment failed → the system put แท็กแดง "ชำระเงินไม่ผ่าน" on (api/app/webchat/paytag.py,
  // owner 27 ก.ย. 69); the customer tab says why. The tag is the one migration 0028 seeds (not a starter label here).
  const web107 = db.conversations.get('c107')
  if (web107) {
    web107.tags = [{ id: 't28', name: 'ชำระเงินไม่ผ่าน', tone: 'crit', auto: 'webchat:pay_failed', added_at: new Date(now - 2 * 60_000).toISOString() }]
    web107.tag = { kind: 'crit', label: 'ชำระเงินไม่ผ่าน' }
  }

  // Jev recognised "ขอเมนู": the answer library's /menu is offered instead of an AI-written draft (round 6, A+B)
  build(db, {
    id: 'c108',
    name: 'คุณเฟิร์น',
    initial: 'ฟ',
    channel: 'line',
    staff: 'jit',
    unread: 1,
    thread: [[50, 'in', 'ขอดูเมนูหน่อยค่ะ']],
    ai: {
      close_chance: null,
      next_steps: steps(['ส่งเมนูและลิงก์สั่งซื้อ', 'คำตอบสำเร็จรูป /menu พร้อมรูปเมนู', true]),
      draft: 'สั่งได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order (สั่งทางเว็บได้ถึง 21.30 น. สำหรับรอบส่งวันถัดไปค่ะ)',
      canned: CANNED,
      cart: null,
      quick: { canned_id: 'cr1', shortcut: 'menu', title: 'เมนูอาหาร', intent: 'menu', confidence: 0.94 },
      draft_source: 'canned',
    },
  })

  // The customer answered our reply with a sticker: the AI deliberately writes no draft and says why (round 8)
  build(db, {
    id: 'c109',
    name: 'คุณมิ้นท์',
    initial: 'ม',
    channel: 'line',
    staff: 'siri',
    unread: 1,
    thread: [
      [26 * MIN, 'in', 'ของจะส่งพรุ่งนี้ใช่ไหมคะ'],
      [24 * MIN, 'staff', 'ใช่ค่ะ จัดส่งพรุ่งนี้รอบ 11.00-12.00 น. นะคะ', 'siri'],
      [15, 'in-stk', 'sticker'],
    ],
    ai: {
      close_chance: null,
      next_steps: [],
      draft: null,
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: null,
      no_draft_reason: 'ลูกค้าส่งสติกเกอร์รับทราบ ไม่ต้องตอบก็ได้',
    },
  })

  // The owner's Messenger test (round 8b): /menu (the order link) went out, the customer still wants the list →
  // the draft is the menu list built by code from the approved "เมนูรอบนี้", with the round's poster attached
  const poster = createMockMedia(now).filter((m) => m.id === 'md6')
  build(db, {
    id: 'c110',
    name: 'Oat (ทดสอบ)',
    initial: 'O',
    channel: 'fb',
    staff: 'jit',
    unread: 1,
    thread: [
      [4 * MIN, 'in', 'ขอรายชื่อเมนุเดือนนี้ครับ'],
      [3 * MIN, 'staff', 'สั่งได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order (สั่งทางเว็บได้ถึง 21.30 น. สำหรับรอบส่งวันถัดไปค่ะ)', 'jit'],
      [40, 'in', 'ส่งเมนูมาเลยดีกว่า'],
    ],
    ai: {
      close_chance: null,
      next_steps: steps(['ส่งรายชื่อเมนูรอบนี้', 'ร่างจากเมนูที่อนุมัติแล้ว พร้อมโปสเตอร์', true]),
      draft: MENU_ROUND_DRAFT,
      canned: CANNED,
      cart: null,
      quick: null,
      draft_source: 'menu_round',
      media: poster,
      menu_round: { round_id: '12', dish_count: 12, valid_to: new Date(now + 35 * DAY * 1000).toISOString().slice(0, 10) },
    },
  })

  // ------------------------------------------------ รอลูกค้า (12): we replied last, < 30 min ago
  const waiting: [string, string, Channel, StaffKey, number, string, string][] = [
    ['c201', 'คุณแพร', 'line', 'ploy', 3, 'มีเมนูไม่ใส่นมไหมคะ', 'มีค่ะ ส่งรายการเมนูไม่มีนมให้ด้านบนแล้วนะคะ'],
    ['c202', 'Nattapong K.', 'fb', 'jit', 5, 'ส่งถึงกี่โมงครับ', 'รอบส่ง 10:00–12:00 ค่ะ'],
    ['c203', 'คุณหญิง', 'line', 'siri', 6, 'โอนแล้วนะคะ', 'ได้รับสลิปแล้วค่ะ ขอบคุณค่ะ'],
    ['c204', 'fah.fitness', 'ig', 'ploy', 8, 'แคลต่อมื้อประมาณเท่าไหร่คะ', 'ประมาณ 350–450 kcal ต่อมื้อค่ะ'],
    ['c205', 'คุณต่อ', 'line', 'siri', 9, 'เลื่อนวันส่งได้ไหมครับ', 'เลื่อนเป็นวันจันทร์ให้แล้วนะคะ'],
    ['c206', 'May Ratchada', 'fb', 'jit', 11, 'อยู่นนทบุรีส่งไหมคะ', 'ส่งนนทบุรีได้ค่ะ ขอรหัสไปรษณีย์ด้วยนะคะ'],
    ['c207', 'คุณออม', 'line', 'siri', 13, 'อุ่นยังไงดีคะ', 'อุ่นไมโครเวฟ 2–3 นาทีได้เลยค่ะ'],
    ['c208', 'คุณเจน', 'line', 'ploy', 15, 'ขอเมนูสัปดาห์หน้าหน่อยค่ะ', 'ส่งเมนูสัปดาห์หน้าให้แล้วนะคะ'],
    ['c209', 'Bank Tanawat', 'fb', 'siri', 18, 'สั่ง 2 แพ็กมีส่วนลดไหมครับ', 'ขอเช็คกับหัวหน้าก่อนนะคะ เดี๋ยวแจ้งกลับค่ะ'],
    ['c210', 'คุณฝน', 'line', 'jit', 21, 'ได้รับของแล้วค่ะ', 'ขอบคุณมากค่ะ ทานให้อร่อยนะคะ'],
    ['c211', 'nina.eats', 'ig', 'ploy', 24, 'ขอดูรีวิวหน่อยค่ะ', 'ส่งรีวิวลูกค้าให้ดูแล้วนะคะ'],
    ['c212', 'คุณกิ๊ก', 'line', 'siri', 27, 'ขอเลขพัสดุด้วยค่ะ', 'ส่งเลขพัสดุ DHL ให้ด้านบนแล้วนะคะ'],
  ]
  const MENU_READ_DEMO: [number, 'in' | 'in-img', string][] = [
    [300, 'in', 'P1 x2\nโคชูจัง 3 กล่อง\nแซลมอนย่าง 2\nสเต๊กหมูพริกไทยดำ\nที่เหลือคละเอาแต่ไก่นะคะ'],
    [200, 'in-img', 'รูปเมนูที่ติ๊กไว้'],
    [120, 'in', 'ติ๊กไว้ในรูปนะคะ เอาแพ็ก Pro 7 วัน 21 กล่องค่ะ'],
  ]
  for (const [id, name, channel, staff, mins, q, a] of waiting) {
    build(db, {
      id,
      name,
      channel,
      staff,
      thread: [
        // อ่านเมนูจากแชท demo (28 ก.ย. 69): a typed menu list, then a ticked menu sheet with the pack said after it
        ...(id === 'c206' ? MENU_READ_DEMO.map(([ago, kind, text]) => [mins * MIN + 150 + ago, kind, text] as Line) : []),
        [mins * MIN + 150, 'in', q],
        [mins * MIN, 'staff', a, staff],
      ],
    })
  }

  // ------------------------------------------------ ทักตาม (5): silent past 60 min (auto-closed at 30)
  const follow: [string, string, Channel, StaffKey, number, string, string, boolean][] = [
    ['c301', 'คุณนุ่น', 'line', 'ploy', 75 * MIN, 'ขอคิดดูก่อนนะคะ', 'ได้เลยค่ะ สนใจทักมาได้ตลอดนะคะ', false],
    ['c302', 'Arm Chaiya', 'fb', 'jit', 2 * HOUR, 'แพ็ก 7 วันราคาเท่าไหร่ครับ', 'แพ็ก 7 วัน 2 มื้อ/วัน 2,190 บาทค่ะ ดูเมนูได้ที่ cleanfit.cloud/order', true],
    ['c303', 'คุณปอ', 'line', 'siri', 3 * HOUR, 'ขอถามแฟนก่อนค่ะ', 'ได้เลยค่ะ', false],
    ['c304', 'june.k', 'ig', 'waranya', 4 * HOUR, 'ส่งต่างจังหวัดไหมคะ', 'ส่งทั่วประเทศค่ะ ขอรหัสไปรษณีย์ด้วยนะคะ', false],
    ['c305', 'คุณโอ๊ต', 'line', 'ploy', 6 * HOUR, 'ขอดูเมนูก่อนครับ', 'ส่งเมนูให้แล้วนะคะ', false],
  ]
  for (const [id, name, channel, staff, ago, q, a, byAi] of follow) {
    build(db, {
      id,
      name,
      channel,
      staff,
      status: 'closed',
      thread: [
        [ago + 200, 'in', q],
        byAi ? [ago, 'ai', a, 'เมนู/ราคา'] : [ago, 'staff', a, staff],
      ],
      ai: {
        close_chance: 35,
        next_steps: steps(['ทักตามพร้อมเมนูสัปดาห์นี้', null]),
        // the follow-up text lives in the follow-up card (api/collab): the reply draft stays empty, like the API
        // (no draft when the shop spoke last)
        draft: null,
        canned: CANNED,
        cart: null,
        quick: null,
        draft_source: null,
      },
    })
  }

  // ------------------------------------------------ ปิดแล้ว (8)
  const closed: [string, string, Channel, ConversationKind, StaffKey, number, string, string][] = [
    ['c401', 'คุณนิด', 'line', 'dm', 'ploy', 4 * HOUR, 'โอนแล้วค่ะ', 'ได้รับแล้วค่ะ ขอบคุณค่ะ'],
    ['c402', 'Tee Wit', 'fb', 'dm', 'jit', DAY, 'สั่งแพ็กเดิมครับ', 'ลงออเดอร์ให้แล้วค่ะ'],
    ['c403', 'คุณแนน', 'line', 'dm', 'siri', 2 * DAY, 'ขอบคุณค่ะ', 'ยินดีค่ะ'],
    ['c404', 'som.o', 'ig', 'dm', 'waranya', 3 * DAY, 'ได้ของแล้วค่ะ', 'ขอบคุณค่ะ'],
    ['c405', 'คุณเก่ง', 'line', 'dm', 'ploy', 5 * DAY, 'ยกเลิกออเดอร์ได้ไหมครับ', 'ยกเลิกให้แล้วนะคะ'],
    ['c406', 'คอมเมนต์ใต้โพสต์', 'fb', 'comment', 'waranya', DAY + 2 * HOUR, 'ราคาเท่าไหร่คะ', 'ตอบทางแชทแล้วนะคะ'],
    ['c407', 'ผู้เยี่ยมชมเว็บ', 'web', 'dm', 'jit', 2 * DAY, 'ชำระเงินแล้วค่ะ', 'ได้รับแล้วค่ะ'],
    ['c408', 'คุณปุ๊ก', 'line', 'dm', 'siri', 5 * HOUR, 'รับทราบค่ะ', 'ขอบคุณค่ะ'],
  ]
  for (const [id, name, channel, kind, staff, ago, q, a] of closed) {
    build(db, {
      id,
      name,
      channel,
      kind,
      staff,
      status: 'closed',
      followUp: false,
      thread: [
        [ago + 300, 'in', q],
        [ago, 'staff', a, staff],
      ],
    })
  }

  // ------------------------------------------------ AI ดูแลแชท (ทดสอบ) — docs/ai-mode.md
  // c150: the AI is in charge mid-sale (test LINE OA) · c151: a fresh hand-off "ลูกค้าถามส่วนลด" (test FB page)
  build(db, {
    id: 'c150',
    name: 'แพรว (บัญชีทดสอบ)',
    initial: 'พ',
    channel: 'line',
    staff: 'ploy',
    followUp: false,
    thread: [
      [27 * MIN, 'in', 'สวัสดีค่ะ สนใจแพ็กอาหารคลีนค่ะ'],
      [26 * MIN, 'sys', 'ai_on', null, 'ploy'],
      [25 * MIN, 'bot', 'สวัสดีค่ะ ยินดีต้อนรับสู่ Cleanfit ค่ะ สนใจแพ็กกี่วันดีคะ'],
      [21 * MIN, 'in', 'แพ็ก 7 วัน 2 มื้อค่ะ ส่งบางนาได้ไหมคะ'],
      [20 * MIN, 'bot', 'ส่งเขตบางนาได้ค่ะ อยากเลือกเมนูเองไหมคะ ถ้าเลือกเองกดลิงก์นี้ เลือกแล้วคัดลอกรหัสกลับมาในแชทได้เลยค่ะ cleanfit.cloud/order/beta'],
      [8 * MIN, 'in', 'Select Menu ID "A7K2Q"\nแพ็ก 7 วัน 2 มื้อ/วัน\nอกไก่ย่างเทริยากิ x4\nแซลมอนย่างซีอิ๊วญี่ปุ่น x4\nปลาดอลลี่นึ่งมะนาว x3\nสเต๊กหมูพริกไทยดำ x3'],
      [7 * MIN, 'bot', 'ได้รับรายการเมนูแล้วค่ะ ครบ 14 มื้อค่ะ'],
      [7 * MIN - 20, 'bot', 'รบกวนขอชื่อ เบอร์โทร และที่อยู่จัดส่งด้วยนะคะ'],
    ],
  })
  build(db, {
    id: 'c151',
    name: 'Nok Sirin (ทดสอบ)',
    initial: 'N',
    channel: 'fb',
    staff: 'ploy',
    thread: [
      [16 * MIN, 'in', 'สนใจแพ็ก 14 วันค่ะ'],
      [15 * MIN, 'sys', 'ai_on', null, 'jit'],
      [14 * MIN, 'bot', 'ได้เลยค่ะ แพ็ก 14 วันมีแบบ 1 มื้อ/วัน และ 2 มื้อ/วันค่ะ สนใจแบบไหนดีคะ'],
      [3 * MIN, 'in', 'ถ้าสั่ง 2 แพ็กพร้อมกัน มีส่วนลดไหมคะ'],
      [2 * MIN + 50, 'handoff', 'ลูกค้าถามส่วนลด', 'ploy'],
    ],
  })
  // ------------------------------------------------ สอน AI ด้วย /ln (api/app/ai/learn.py) — c156 (test FB page, AI in charge)
  // an earlier teaching the owner approved + the one from the owner's screenshot 27 ก.ย. (waiting, AI-rewritten,
  // it may contradict a rule that is already in use)
  build(db, {
    id: 'c156',
    name: 'มายด์ (บัญชีทดสอบ)',
    initial: 'ม',
    channel: 'fb',
    staff: 'ploy',
    followUp: false,
    thread: [
      [15 * MIN, 'in', 'สวัสดีค่ะ'],
      [14 * MIN + 40, 'sys', 'ai_on', null, 'ploy'],
      [14 * MIN, 'bot', 'สวัสดีค่ะ ยินดีต้อนรับสู่ Cleanfit นะคะ มีอะไรให้แอดมินช่วยดูแลคะ'],
      [10 * MIN, 'in', 'สนใจแพ็กเกจค่ะ'],
      [9 * MIN, 'bot', 'คุณลูกค้าสนใจรับเป็นแพ็กเกจกี่มื้อดีคะ แอดมินจะได้แนะนำเมนูให้เหมาะกับคุณลูกค้านะคะ'],
    ],
  })
  addLearnCards(db, 'c156', [
    {
      at: 12 * MIN,
      text: 'ทักครั้งแรกให้แนะนำตัวสั้นๆ แล้วถามว่าสนใจแพ็กแบบไหน',
      answering: 'สวัสดีค่ะ ยินดีต้อนรับสู่ Cleanfit นะคะ มีอะไรให้แอดมินช่วยดูแลคะ',
      rule: 'เมื่อลูกค้าทักครั้งแรก ให้ทักทายสั้นๆ แล้วถามว่าสนใจแพ็กแบบไหน',
      status: 'approved',
    },
    {
      at: 7 * MIN,
      text: 'ถามกี่มื้อแล้วควรบอกว่ามีแพ็คเกจไหนบ้าง',
      answering: 'คุณลูกค้าสนใจรับเป็นแพ็กเกจกี่มื้อดีคะ แอดมินจะได้แนะนำเมนูให้เหมาะกับคุณลูกค้านะคะ',
      rule: 'เมื่อถามลูกค้าว่าสนใจแพ็กเกจกี่มื้อ ให้บอกแพ็กเกจที่มีให้เลือกพร้อมกันเลย (ชื่อแพ็ก จำนวนมื้อ ราคา จากแคตตาล็อก) แทนการถามลอยๆ',
      status: 'pending_approval',
      overlaps: [
        {
          rule_id: 'r3',
          relation: 'conflict',
          why: 'กติกาเดิมให้ถามทีละเรื่อง ข้อใหม่ให้บอกหลายแพ็กพร้อมกัน',
          text: 'ถามความต้องการของลูกค้าทีละ 1 เรื่อง ไม่ให้ข้อมูลเยอะเกินในครั้งเดียว',
          status: 'approved',
        },
      ],
    },
  ])

  // ------------------------------------------------ เบอร์ในข้อความลูกค้า → "ผูกเบอร์ … กับลูกค้า POS" (lib/phone.ts)
  // POS side of these chats: api/pos/mock.ts (CUSTOMERS / CONVS)
  // c152 unlinked, 1 POS customer has the number · c153 unlinked, 2 numbers: none in POS / a shared landline (2 customers)
  // c154 linked to the customer with that number (ผูกแล้ว) · c155 linked to someone else (เปลี่ยนไปผูก)
  build(db, {
    id: 'c152',
    name: 'ฝน (ทดสอบ)',
    initial: 'ฝ',
    channel: 'line',
    staff: 'ploy',
    thread: [
      [40 * MIN, 'in', 'สนใจแพ็ก 7 วันค่ะ'],
      [38 * MIN, 'staff', 'ได้เลยค่ะ รบกวนขอเบอร์โทรด้วยนะคะ', 'ploy'],
      [16 * MIN, 'in', 'เบอร์ 085-883-6069 ค่ะ'],
      [15 * MIN, 'staff', 'รับทราบค่ะ เดี๋ยวแอดมินลงข้อมูลให้นะคะ', 'ploy'],
    ],
  })
  build(db, {
    id: 'c153',
    name: 'คุณนุ่น (ทดสอบ)',
    initial: 'น',
    channel: 'fb',
    staff: 'jit',
    thread: [
      [14 * MIN, 'in', 'โทร 0891234567 หรือเบอร์ออฟฟิศ 02-123-4567 ได้เลยค่ะ'],
      [13 * MIN, 'staff', 'ขอบคุณค่ะ', 'jit'],
    ],
  })
  build(db, {
    id: 'c154',
    name: 'Ton (ทดสอบ)',
    channel: 'line',
    staff: 'siri',
    thread: [
      [12 * MIN, 'in', 'เบอร์เดิมครับ 081-222-3344'],
      [11 * MIN, 'staff', 'ได้เลยค่ะ', 'siri'],
    ],
  })
  build(db, {
    id: 'c155',
    name: 'Pim (ทดสอบ)',
    channel: 'fb',
    staff: 'ploy',
    thread: [
      [10 * MIN, 'in', 'ใช้เบอร์ใหม่นะคะ 081-234-4521'],
      [9 * MIN, 'staff', 'รับทราบค่ะ', 'ploy'],
    ],
  })

  const aiNow = db.conversations.get('c150')
  if (aiNow) aiNow.ai_mode = { session_id: 's150', by: ref('ploy'), since: new Date(now - 26 * MIN * 1000).toISOString(), handoff: null }
  const aiLearn = db.conversations.get('c156')
  if (aiLearn) aiLearn.ai_mode = { session_id: 's156', by: ref('ploy'), since: new Date(now - (14 * MIN + 40) * 1000).toISOString(), handoff: null }
  const aiHand = db.conversations.get('c151')
  if (aiHand) aiHand.ai_handoff = { code: 'discount', note: 'ลูกค้าถามส่วนลด', at: new Date(now - (2 * MIN + 50) * 1000).toISOString() }

  for (const [id, ids] of [['c201', ['t3']], ['c204', ['t5', 't4']], ['c302', ['t4']], ['c206', ['t2']]] as const) {
    const c = db.conversations.get(id)
    if (c) applyMockTags(c, [...ids])
  }

  for (const [id, c] of db.conversations) {
    if (!db.orderDrafts.has(id)) db.orderDrafts.set(id, genericDraft(c.pos))
  }

  db.team = (['ploy', 'jit', 'siri', 'waranya', 'siriporn'] as const).map((k) => ({
    ...ref(k),
    initial: STAFF[k].initial,
    available: k !== 'siriporn',
    open_count: 0,
    cap: 10,
  }))
  // "ล้างแชทนี้ (ทดสอบ)" (api/app/devreset.py): the server allows it only on test-account chats for owner/head
  for (const id of ['c150', 'c151', 'c156']) {
    const c = db.conversations.get(id)
    if (c) c.dev_resettable = true
  }
  return db
}

interface LearnSpec {
  at: number
  text: string
  answering: string
  rule: string
  status: 'pending_approval' | 'approved'
  overlaps?: LearnItem['overlaps']
}

/** "/ln" cards (api/app/ai/learn.py) placed in a mock thread at their time, after the AI message they answer. */
function addLearnCards(db: MockDb, convId: string, cards: LearnSpec[]): void {
  const items = db.threads.get(convId)
  if (!items) return
  const iso = (ago: number) => new Date(db.now - ago * 1000).toISOString()
  cards.forEach((c, i) => {
    const answering = [...items]
      .reverse()
      .find((x) => x.kind === 'message' && x.direction === 'out' && x.at < iso(c.at) && x.text === c.answering)
    items.push({
      kind: 'learn',
      id: `${convId}-l${i}`,
      conversation_id: convId,
      at: iso(c.at),
      text: c.text,
      answering: answering ? { message_id: answering.id, author: 'ai', name: null, text: c.answering, at: answering.at } : null,
      rewrite: 'done',
      ai_text: c.rule,
      rule: {
        id: `${convId}-rule${i}`,
        text: c.rule,
        status: c.status,
        pending_text: null,
        pending_by: null,
        edited: false,
        edited_by: null,
        approved_by: c.status === 'approved' ? 'เจ้าของ' : null,
        approved_at: c.status === 'approved' ? iso(c.at - 60) : null,
      },
      note: null,
      unsafe: false,
      overlaps: c.overlaps ?? [],
    })
  })
  items.sort((a, b) => a.at.localeCompare(b.at))
}

/** Incoming messages the realtime simulator sends every ~20 s. */
export const SIM_TEXTS = [
  'ยังรับออเดอร์วันนี้อยู่ไหมคะ',
  'ค่าส่งไปลาดพร้าวเท่าไหร่ครับ',
  'ขอเมนูสัปดาห์นี้หน่อยค่ะ',
  'แพ็ก 7 วันมีกี่เมนูคะ',
  'โอนแล้วนะคะ เดี๋ยวส่งสลิปให้',
  'ของถึงแล้วค่ะ ขอบคุณค่ะ',
  'มีเมนูคีโตไหมคะ',
  'ส่งพรุ่งนี้เช้าได้ไหมครับ',
]

export const SIM_NEW_CONTACTS: [string, Channel][] = [
  ['คุณแตง', 'line'],
  ['Ton Sakda', 'fb'],
  ['bee.healthy', 'ig'],
  ['ผู้เยี่ยมชมเว็บ', 'web'],
]

/** Settings page "AI" block in mock mode (shapes of GET /api/v1/ai/config; library answers are examples). */
export const MOCK_AI_CONFIG: AiConfig = {
  settings: {
    auto_send: false,
    stop_all: false,
    drafts: true,
    threshold: 0.8,
    groups: { shipping: true, menu: true, care_status: true },
    hours: { start: '00:00', end: '24:00' },
    delay_sec: { min: 5, max: 15 },
    no_interrupt_min: 10,
    hold_message: false,
    staff_style_map: {},
    order_cutoff: { chat: '22:00', web: '21:30' },
    freezer_days: 120,
    quick_answers: true,
    quick_threshold: 0.8,
    auto_draft: false,
    // AI ดูแลแชท (ทดสอบ) — docs/ai-mode.md: on for the test accounts, payment info not typed in yet
    sales_enabled: true,
    sales_allowlist_only: true,
    sales_menu_url: 'https://cleanfit.cloud/order/beta',
    sales_payment_text: '',
    sales_payment_text_en: '',
    sales_payment_media_id: '',
    sales_slip_test_pass: false,
    sales_max_turns: 40,
    menu_read: true,
    script_live: true,
  },
  can_edit: true,
  ai_available: true,
  quick: {
    map: [
      {
        intent: 'ship_fee',
        intent_label: 'ค่าส่ง',
        group: 'shipping',
        live: true,
        replies: [{ id: '6', shortcut: 'zip', title: 'ขอรหัสไปรษณีย์' }],
      },
      { intent: 'ship_area', intent_label: 'พื้นที่ส่ง', group: 'shipping', replies: [{ id: '5', shortcut: 'dhl', title: 'ส่งต่างจังหวัด (DHL)' }] },
      { intent: 'menu', intent_label: 'เมนู', group: 'menu', replies: [{ id: '1', shortcut: 'menu', title: 'เมนูสัปดาห์นี้' }] },
      { intent: 'order_link', intent_label: 'ลิงก์สั่ง', group: 'menu', replies: [{ id: '2', shortcut: 'link', title: 'ส่งลิงก์สั่งซื้อ' }] },
      {
        intent: 'storage',
        intent_label: 'เก็บรักษา',
        group: 'care_status',
        replies: [
          { id: '3', shortcut: 'เก็บ', title: 'เก็บรักษา: ช่องฟรีซ / ช่องธรรมดา' },
          { id: '4', shortcut: 'อุ่น', title: 'วิธีอุ่น' },
        ],
      },
    ],
    answers: 7,
    week: { quick_runs: 184, ai_runs: 412, saved_usd: 0.36, saved_thb: 12.4, cost_usd: 0.95 },
  },
  slip: { auto: false, configured: true, can_edit: true },
  sales: { send_mode: 'allowlist', allowlist_count: 3, ready: true },
}
